import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";
const lbl = "text-[11px] uppercase tracking-widest text-ink-soft";

const blank = (date) => ({
  lock_type: "admin_hold", branch_id: "", title: "", reason: "", other_reason: "", notes: "",
  rental_date: date, start_time: "10:00", all_day: false, duration_hours: 4, end_date: "",
  quantity: 1, unit_mode: "auto", unit_ids: [], hold_hours: 24,
  contact_name: "", contact_whatsapp: "",
});

const preferredHub = (branches, wanted) =>
  wanted || branches.find((b) => (b.units_total || 0) > 0)?.id || branches[0]?.id || "";

export const LockForm = ({ open, onOpenChange, meta, branches, defaultDate, defaultType,
  defaultBranchId, onSaved }) => {
  const [f, setF] = useState(blank(defaultDate));
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  useEffect(() => {
    if (!open) return;
    setF({ ...blank(defaultDate), lock_type: defaultType || "admin_hold",
      branch_id: preferredHub(branches, defaultBranchId) });
    api.get("/units").then((r) => setUnits(r.data || [])).catch(() => setUnits([]));
  }, [open, defaultDate, defaultType, defaultBranchId, branches]);

  const typeMeta = useMemo(
    () => (meta.lock_types || []).find((t) => t.key === f.lock_type) || { reasons: [], hint: "" },
    [meta, f.lock_type]);
  const hubUnits = useMemo(
    () => units.filter((u) => !f.branch_id || u.branch_id === f.branch_id), [units, f.branch_id]);

  const toggleUnit = (id) => set({
    unit_ids: f.unit_ids.includes(id) ? f.unit_ids.filter((x) => x !== id) : [...f.unit_ids, id],
  });

  const save = async () => {
    const reason = f.reason === "Lainnya" ? f.other_reason.trim() : f.reason;
    if (!reason) { toast.error("Pilih atau tulis alasannya dulu"); return; }
    setBusy(true);
    try {
      const body = {
        lock_type: f.lock_type, branch_id: f.branch_id || undefined, title: f.title, reason,
        notes: f.notes, rental_date: f.rental_date, start_time: f.start_time,
        all_day: f.all_day, end_date: f.end_date || undefined,
        duration_hours: f.all_day ? undefined : Number(f.duration_hours),
        quantity: f.unit_mode === "manual" ? f.unit_ids.length || 1 : Number(f.quantity),
        unit_ids: f.unit_mode === "manual" ? f.unit_ids : undefined,
        hold_hours: f.lock_type === "admin_hold" ? Number(f.hold_hours) : undefined,
        contact_name: f.contact_name, contact_whatsapp: f.contact_whatsapp,
      };
      const r = await api.post("/calendar-locks", body);
      toast.success(`${r.data.lock_type_label} dibuat — ${r.data.quantity} meja terkunci`);
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat kunci kalender");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>Kunci kalender</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="lock-form">
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            {(meta.lock_types || []).map((t) => (
              <button key={t.key} data-testid={`lock-type-${t.key}`} onClick={() => set({ lock_type: t.key, reason: "" })}
                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                  f.lock_type === t.key ? "border-ink bg-ink text-cream" : "border-black/15 hover:border-ink"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <p className="sm:col-span-2 -mt-1 text-[11px] text-ink-soft">{typeMeta.hint}</p>

          <label className="block"><span className={lbl}>Hub</span>
            <select className={`${sel} mt-1`} data-testid="lock-branch" value={f.branch_id}
              onChange={(e) => set({ branch_id: e.target.value, unit_ids: [] })}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <label className="block"><span className={lbl}>Alasan</span>
            <select className={`${sel} mt-1`} data-testid="lock-reason" value={f.reason}
              onChange={(e) => set({ reason: e.target.value })}>
              <option value="">Pilih alasan…</option>
              {(typeMeta.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          {f.reason === "Lainnya" && (
            <label className="sm:col-span-2 block"><span className={lbl}>Tulis alasannya</span>
              <Input className="mt-1" data-testid="lock-other-reason" value={f.other_reason}
                onChange={(e) => set({ other_reason: e.target.value })} />
            </label>
          )}
          <label className="sm:col-span-2 block"><span className={lbl}>Judul di kalender (opsional)</span>
            <Input className="mt-1" data-testid="lock-title" value={f.title}
              placeholder="Mis. Hold PT Sinar Jaya — 3 meja" onChange={(e) => set({ title: e.target.value })} />
          </label>

          <label className="block"><span className={lbl}>Tanggal mulai</span>
            <Input type="date" className="mt-1" data-testid="lock-date" value={f.rental_date}
              onChange={(e) => set({ rental_date: e.target.value })} />
          </label>
          <label className="flex items-end gap-2 pb-2">
            <input type="checkbox" data-testid="lock-all-day" checked={f.all_day}
              onChange={(e) => set({ all_day: e.target.checked })} className="h-4 w-4" />
            <span className="text-sm">Sehari penuh</span>
          </label>
          {f.all_day ? (
            <label className="block"><span className={lbl}>Sampai tanggal (opsional)</span>
              <Input type="date" className="mt-1" data-testid="lock-end-date" value={f.end_date}
                onChange={(e) => set({ end_date: e.target.value })} />
            </label>
          ) : (
            <>
              <label className="block"><span className={lbl}>Jam mulai</span>
                <Input type="time" className="mt-1" data-testid="lock-start-time" value={f.start_time}
                  onChange={(e) => set({ start_time: e.target.value })} />
              </label>
              <label className="block"><span className={lbl}>Durasi (jam)</span>
                <Input type="number" min="0.5" step="0.5" className="mt-1" data-testid="lock-duration"
                  value={f.duration_hours} onChange={(e) => set({ duration_hours: e.target.value })} />
              </label>
            </>
          )}

          <label className="block"><span className={lbl}>Pilih meja</span>
            <select className={`${sel} mt-1`} data-testid="lock-unit-mode" value={f.unit_mode}
              onChange={(e) => set({ unit_mode: e.target.value })}>
              <option value="auto">Otomatis (sistem pilih yang bebas)</option>
              <option value="manual">Pilih meja sendiri</option>
            </select>
          </label>
          {f.unit_mode === "auto" ? (
            <label className="block"><span className={lbl}>Jumlah meja</span>
              <Input type="number" min="1" className="mt-1" data-testid="lock-quantity" value={f.quantity}
                onChange={(e) => set({ quantity: e.target.value })} />
            </label>
          ) : (
            <div className="sm:col-span-2">
              <span className={lbl}>Meja yang dikunci ({f.unit_ids.length})</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {hubUnits.map((u) => (
                  <button key={u.id} data-testid={`lock-unit-${u.unit_code}`} onClick={() => toggleUnit(u.id)}
                    className={`rounded-md border px-3 py-1.5 text-xs ${f.unit_ids.includes(u.id)
                      ? "border-ink bg-ink text-cream" : "border-black/15 hover:border-ink"}`}>
                    {u.unit_code}
                  </button>
                ))}
                {hubUnits.length === 0 && <span className="text-xs text-ink-soft">Belum ada unit di Hub ini</span>}
              </div>
            </div>
          )}

          {f.lock_type === "admin_hold" && (
            <>
              <label className="block"><span className={lbl}>Hold berlaku (jam)</span>
                <Input type="number" min="1" className="mt-1" data-testid="lock-hold-hours" value={f.hold_hours}
                  onChange={(e) => set({ hold_hours: e.target.value })} />
                <span className="mt-1 block text-[10px] text-ink-soft">Lewat jam ini, meja otomatis bebas lagi.</span>
              </label>
              <label className="block"><span className={lbl}>Nama kontak (opsional)</span>
                <Input className="mt-1" data-testid="lock-contact" value={f.contact_name}
                  onChange={(e) => set({ contact_name: e.target.value })} />
              </label>
              <label className="block sm:col-span-2"><span className={lbl}>WhatsApp kontak (opsional)</span>
                <Input className="mt-1" data-testid="lock-contact-wa" value={f.contact_whatsapp}
                  onChange={(e) => set({ contact_whatsapp: e.target.value })} />
              </label>
            </>
          )}

          <label className="sm:col-span-2 block"><span className={lbl}>Catatan internal</span>
            <Textarea rows={2} className="mt-1" data-testid="lock-notes" value={f.notes}
              onChange={(e) => set({ notes: e.target.value })} />
          </label>

          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <button data-testid="lock-save" disabled={busy} onClick={save}
              className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
              {busy ? "Menyimpan…" : "Kunci sekarang"}
            </button>
            <button onClick={() => onOpenChange(false)} className="rounded-md border border-black/15 px-5 py-2.5 text-sm">
              Batal
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
